/**
 * Persist which certification "shell" the pemohon is navigating (halal | slhs | haccp)
 * so shared routes (/documents, /gap-assessment, …) keep the correct sidebar and checklist.
 *
 * Intentionally does not import certificationType.js to avoid circular deps if that file re-exports helpers.
 */

export const PEMOHON_CERT_FE_STORAGE_KEY = 'halalin.pemohonCertFeId';

/** @typedef {'halal'|'slhs'|'haccp'} PemohonCertFeId */

const VALID = new Set(['halal', 'slhs', 'haccp']);

/** @param {string|null|undefined} slug */
function certSlugToFeIdLocal(slug) {
  const s = String(slug || '')
    .trim()
    .toLowerCase();
  if (s === 'slhs') return 'slhs';
  if (s === 'haccp') return 'haccp';
  if (s === 'halal') return 'halal';
  return null;
}

/** @param {PemohonCertFeId} feId */
function feCertSlugFromFeIdLocal(feId) {
  if (feId === 'slhs') return 'slhs';
  if (feId === 'haccp') return 'haccp';
  return 'halal';
}

function readStoredFeId() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PEMOHON_CERT_FE_STORAGE_KEY);
    const s = String(raw || '')
      .trim()
      .toLowerCase();
    return VALID.has(s) ? /** @type {PemohonCertFeId} */ (s) : null;
  } catch {
    return null;
  }
}

function writeStoredFeId(feId) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(PEMOHON_CERT_FE_STORAGE_KEY, feId);
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Path implies an explicit package dashboard: /dashboard/halal | slhs | haccp
 * @param {string} pathname
 * @returns {PemohonCertFeId|null}
 */
export function feIdFromDashboardPath(pathname) {
  const p = pathname || '';
  if (p.startsWith('/dashboard/halal')) return 'halal';
  if (p.startsWith('/dashboard/slhs')) return 'slhs';
  if (p.startsWith('/dashboard/haccp')) return 'haccp';
  return null;
}

/**
 * @param {string} pathname
 * @returns {PemohonCertFeId|null}
 */
export function feIdFromPreAssessmentPath(pathname) {
  const p = pathname || '';
  const m = p.match(/^\/pre-assessment\/([^/]+)/i);
  if (!m) return null;
  const fe = certSlugToFeIdLocal(m[1]);
  return fe || null;
}

/**
 * Shared pemohon journey routes (same shell as Halal today): show journey sidebar + use stored fe id.
 * @param {string} pathname
 */
export function isPemohonJourneyShellPath(pathname) {
  const p = pathname || '';
  const prefixes = [
    '/pre-assessment',
    '/gap-assessment',
    '/audit-external',
    '/certificate',
    '/documents',
    '/settings',
  ];
  return prefixes.some((pre) => p === pre || p.startsWith(`${pre}/`));
}

/**
 * When user hits an explicit package or pre-assessment slug, persist for shared routes.
 * @param {string} pathname
 */
export function syncPemohonCertFeIdFromPath(pathname) {
  const fromDash = feIdFromDashboardPath(pathname);
  if (fromDash) {
    writeStoredFeId(fromDash);
    return;
  }
  const fromPre = feIdFromPreAssessmentPath(pathname);
  if (fromPre) {
    writeStoredFeId(fromPre);
  }
}

/**
 * Resolve active fe id for menus and checklist loading.
 * @param {string} pathname
 * @returns {PemohonCertFeId}
 */
export function resolvePemohonCertFeId(pathname) {
  const fromDash = feIdFromDashboardPath(pathname);
  if (fromDash) return fromDash;
  const fromPre = feIdFromPreAssessmentPath(pathname);
  if (fromPre) return fromPre;
  if (isPemohonJourneyShellPath(pathname)) {
    return readStoredFeId() || 'halal';
  }
  return readStoredFeId() || 'halal';
}

/**
 * Ringkasan dashboard path for sidebar label target.
 * @param {PemohonCertFeId} feId
 */
export function dashboardPathForPemohonCertFeId(feId) {
  if (feId === 'slhs') return '/dashboard/slhs';
  if (feId === 'haccp') return '/dashboard/haccp';
  return '/dashboard/halal';
}

/**
 * Human label for sidebar "Ringkasan …"
 * @param {PemohonCertFeId} feId
 */
export function ringkasanLabelForFeId(feId) {
  if (feId === 'slhs') return 'Ringkasan SLHS';
  if (feId === 'haccp') return 'Ringkasan HACCP';
  return 'Ringkasan Halal';
}

export function preAssessmentPathForResolvedFeId(feId) {
  return `/pre-assessment/${feCertSlugFromFeIdLocal(feId)}`;
}

/**
 * Pre-Assessment menu href from current location (package dashboard, pre-assessment, or shared shell).
 * @param {string} pathname
 */
export function preAssessmentHrefForPathname(pathname) {
  const p = pathname || '';
  const m = p.match(/^\/pre-assessment\/([^/]+)/i);
  if (m) {
    const fe = certSlugToFeIdLocal(m[1]);
    if (fe) return `/pre-assessment/${feCertSlugFromFeIdLocal(fe)}`;
  }
  if (p.startsWith('/dashboard/slhs')) return '/pre-assessment/slhs';
  if (p.startsWith('/dashboard/haccp')) return '/pre-assessment/haccp';
  if (p.startsWith('/dashboard/halal')) return '/pre-assessment/halal';
  const fe = resolvePemohonCertFeId(pathname);
  return preAssessmentPathForResolvedFeId(fe);
}
